/**
 * A stand-in bot, used only while src/ai/index.ts does not exist yet.
 *
 * It picks a random legal action, with two nudges that keep a match moving
 * instead of stalling: it prefers to keep its opening hand rather than redraw
 * forever, and it deprioritises Concede so a test match does not end on turn
 * one. It reads nothing but the ctx it is handed, so it obeys the same hidden
 * information rules as a real bot.
 *
 * The moment the AI engineer lands `createBot`, the loader in matchController
 * picks that up and this file stops being used. Delete it once the real bot is
 * in and the fallback is no longer wanted.
 */

import type { Action } from '@/engine';
import type { Bot, BotContext, BotDecision, CreateBotOptions } from '@/ai/types';

/**
 * Lower sorts first. `Concede` never appears in a stub decision at all: the
 * engine offers it even to a player with no priority, so treating it as just
 * another legal action ends matches on turn one.
 */
function priority(action: Action): number {
  return action.type === 'Redraw' ? 5 : 1;
}

export function createStubBot(options: CreateBotOptions): Bot {
  return {
    difficulty: options.difficulty,
    name: `Stub bot (${options.difficulty})`,
    chooseAction(ctx: BotContext): BotDecision {
      const usable = ctx.legal.filter((a) => a.type !== 'Concede');
      const pool = usable.length > 0 ? usable : ctx.legal;
      const best = Math.min(...pool.map(priority));
      const choices = pool.filter((a) => priority(a) === best);
      const action = choices[Math.floor(ctx.random() * choices.length) % choices.length];
      return { action, reason: 'Random legal action (stub bot)' };
    },
  };
}
